import type { SelectableChartType } from '../dashboard-capabilities/types/chart';
import type { SecondaryDimensionValues } from '../dashboard-capabilities/types/dimension';
import type { MetricValues } from '../dashboard-capabilities/types/metric';
import { CHART_INPUT_REQUIREMENTS } from './chart-input-requirements';
import { DataType } from './data-type';
import {
  getMetricDataTypeCapability,
  METRIC_DATA_TYPE_REGISTRY,
} from './metric-registry';

export type DataTypeResolutionResult =
  | { compatible: true; resolvedDataType: DataType }
  | { compatible: false; reason: string };

/**
 * Presentation order for chart types when deriving allowed lists.
 * Keeps output aligned with historical matrix ordering where possible.
 * Non-selectable legacy chart types (for example, share-of-segment)
 * are intentionally excluded from this list.
 */
function isSelectableChartExcludedForMetric(
  chartType: SelectableChartType,
  metric: MetricValues
): boolean {
  const excluded =
    getMetricDataTypeCapability(metric).excludedSelectableChartTypes;
  return excluded !== undefined && excluded.includes(chartType);
}

const CHART_TYPE_PRESENTATION_ORDER: SelectableChartType[] = [
  'bar',
  'diverging-bar',
  'diverging-bar-combo',
  'line',
  'volume',
  'stacked-bar',
  'grouped-bar',
  'table',
  'treemap',
  'sankey',
  'distribution',
  'box',
  'map',
  'text',
  'gauge',
  'ai-summary',
];

export function resolveDataType(
  chartType: SelectableChartType,
  metric: MetricValues,
  secondaryDimension?: SecondaryDimensionValues
): DataTypeResolutionResult {
  if (isSelectableChartExcludedForMetric(chartType, metric)) {
    return {
      compatible: false,
      reason: `Chart "${chartType}" is not supported for metric "${String(metric)}".`,
    };
  }

  const requiredOrdered = CHART_INPUT_REQUIREMENTS[chartType];
  const { dataTypes: availableDataTypes } = getMetricDataTypeCapability(metric);
  const available = new Set(availableDataTypes);

  const intersectionOrdered = requiredOrdered.filter((dt) => available.has(dt));

  if (intersectionOrdered.length === 0) {
    return {
      compatible: false,
      reason: `No DataType overlap between chart "${chartType}" and metric "${metric}".`,
    };
  }

  const preferTemporal = secondaryDimension === 'timeseries';
  const preferSegmented =
    secondaryDimension !== undefined && secondaryDimension !== 'timeseries';

  if (preferTemporal) {
    const temporalPick = intersectionOrdered.find(
      (dt) => dt === DataType.TIMESERIES || dt === DataType.BREAKDOWN_TIMESERIES
    );
    if (temporalPick !== undefined) {
      return { compatible: true, resolvedDataType: temporalPick };
    }
  }

  if (preferSegmented) {
    const segmentedPick = intersectionOrdered.find(
      (dt) =>
        dt === DataType.BREAKDOWN_SINGLE || dt === DataType.BREAKDOWN_TIMESERIES
    );
    if (segmentedPick !== undefined) {
      return { compatible: true, resolvedDataType: segmentedPick };
    }
  }

  const first = intersectionOrdered[0];
  if (first === undefined) {
    return {
      compatible: false,
      reason: 'Resolved intersection was unexpectedly empty.',
    };
  }

  return { compatible: true, resolvedDataType: first };
}

export function getCompatibleMetricsForChart(
  chartType: SelectableChartType
): MetricValues[] {
  const required = new Set(CHART_INPUT_REQUIREMENTS[chartType]);
  return (Object.keys(METRIC_DATA_TYPE_REGISTRY) as MetricValues[]).filter(
    (m) => {
      if (isSelectableChartExcludedForMetric(chartType, m)) {
        return false;
      }
      const { dataTypes } = getMetricDataTypeCapability(m);
      return dataTypes.some((dt) => required.has(dt));
    }
  );
}

export function getCompatibleChartsForMetric(
  metric: MetricValues
): SelectableChartType[] {
  const available = new Set(getMetricDataTypeCapability(metric).dataTypes);
  return CHART_TYPE_PRESENTATION_ORDER.filter((chartType) => {
    if (isSelectableChartExcludedForMetric(chartType, metric)) {
      return false;
    }
    return CHART_INPUT_REQUIREMENTS[chartType].some((dt) => available.has(dt));
  });
}
